import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { USE_EMULATOR as USE_FIRESTORE_EMULATOR } from '@angular/fire/compat/firestore';
import {
  REGION,
  USE_EMULATOR as USE_FUNCTIONS_EMULATOR,
} from '@angular/fire/compat/functions';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { GoogleMapsModule } from '@angular/google-maps';
import {
  MAT_DATE_LOCALE,
  MatNativeDateModule,
  NativeDateModule,
} from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { Title } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { RouterState, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule, USER_PROVIDED_META_REDUCERS } from '@ngrx/store';
import {
  AuthProcessService,
  MultifactorAwareAuthProcessService,
  NgAuthModule,
} from '@principle-theorem/ng-auth';
import { NgEditorModule } from '@principle-theorem/ng-editor';
import { NgFeatureFlagsModule } from '@principle-theorem/ng-feature-flags';
import {
  NgIntercomComponentsModule,
  NgIntercomModule,
} from '@principle-theorem/ng-intercom';
import { NgMaterialModule } from '@principle-theorem/ng-material';
import {
  NG_MEDIPASS_CONFIG_PROVIDER,
  NgMedipassModule,
} from '@principle-theorem/ng-medipass';
import { NgNotificationsModule } from '@principle-theorem/ng-notifications';
import { NgPaymentsModule } from '@principle-theorem/ng-payments';
import {
  MedipassOrgConfigProvider,
  NgPrincipleModule,
  TyroOrgConfigProvider,
} from '@principle-theorem/ng-principle';
import {
  BasicRouterStateSerializer,
  CurrentScopeEffects,
  CurrentScopeFacade,
  MAPS_CONFIG,
  NgPrincipleSharedModule,
  OrganisationService,
  principleCoreMetaReducers,
  principleCoreReducers,
  PrincipleLoggerMetadataResolverService,
} from '@principle-theorem/ng-principle-shared';
import {
  AppVersionModule,
  FIREBASE_PROJECT_ID_PROVIDER,
  GCloudErrorHandlerService,
  LOGGER_METADATA_RESOLVER,
  NgSharedModule,
  ORGANISATION_SERVICE,
  RecaptchaModule,
} from '@principle-theorem/ng-shared';
import { NgSlackIntegrationModule } from '@principle-theorem/ng-slack-integration';
import { NgStripeModule } from '@principle-theorem/ng-stripe';
import { NG_TYRO_CONFIG_PROVIDER } from '@principle-theorem/ng-tyro';
import { NOTIFICATION_PROVIDERS } from '@principle-theorem/principle-core';
import { ALL_FEATURES } from '@principle-theorem/principle-core/features';
import {
  firebaseFunctionUrl,
  FUNCTIONS_REGION,
} from '@principle-theorem/shared';
import { IntercomModule } from '@supy-io/ngx-intercom';
import { HotkeyModule } from 'angular2-hotkeys';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';

const LEGACY_MODULE_PROVIDERS = importProvidersFrom(
  NgAuthModule.forRoot(environment.ngAuth),
  NgIntercomModule.forRoot(environment.intercom),
  IntercomModule.forRoot({
    appId: environment.intercom.appId,
    updateOnRouterChange: true,
  }),
  NgIntercomComponentsModule,
  NgPrincipleModule.forRoot({
    production: environment.production,
    tyro: environment.tyro,
    medipass: environment.medipass,
    apiDocsUrl: environment.apiDocsUrl,
    partnersAppUrl: environment.partnersAppUrl,
  }),
  NgSlackIntegrationModule.forRoot({
    projectId: environment.firebase.projectId,
    slack: { clientId: environment.slack.clientId },
  }),
  GoogleMapsModule,
  NgFeatureFlagsModule.forRoot({
    production: environment.production,
    features: ALL_FEATURES,
  }),
  StoreModule.forRoot(principleCoreReducers, {
    ...principleCoreMetaReducers,
    runtimeChecks: {
      strictStateImmutability: true,
      strictActionImmutability: true,
      strictStateSerializability: true,
      strictActionSerializability: true,
      strictActionTypeUniqueness: true,
    },
  }),
  EffectsModule.forRoot([CurrentScopeEffects]),
  NgMedipassModule,
  StoreRouterConnectingModule.forRoot({
    serializer: BasicRouterStateSerializer,
    routerState: RouterState.Minimal,
  }),
  NgEditorModule.forRoot(),
  NgNotificationsModule.forRoot({ providers: NOTIFICATION_PROVIDERS }),
  NgStripeModule.forRoot(environment.stripe),
  NgSharedModule.forRoot({
    stackdriver: environment.stackdriver,
    appUrl: environment.appUrl,
    production: environment.production,
  }),
  NgPrincipleSharedModule.forRoot({
    projectId: environment.firebase.projectId,
    podium: { clientId: environment.podium.clientId },
    typesense: environment.typesense,
  }),
  AppVersionModule.forRoot(environment.version),
  NgMaterialModule,
  NativeDateModule,
  MatNativeDateModule,
  NgPaymentsModule.forRoot({
    medipass: {
      webhookEndpoint: firebaseFunctionUrl(
        FUNCTIONS_REGION,
        environment.firebase.projectId,
        'http-medipass-webhookHandler'
      ),
    },
    hicapsConnect: {
      PmsName: environment.hicapsConnect.PmsName,
      PmsKey: environment.hicapsConnect.PmsKey,
    },
  }),
  HotkeyModule.forRoot({ disableCheatSheet: true }),
  MonacoEditorModule.forRoot(),
  RecaptchaModule.forRoot({ siteKey: environment.recaptcha.siteKey })
);

const ROOT_SERVICES = [
  { provide: FIREBASE_OPTIONS, useValue: environment.firebase },
  provideFirebaseApp(() => initializeApp(environment.firebase)),
  { provide: REGION, useValue: FUNCTIONS_REGION },
  provideFirestore(() => getFirestore()),
  provideStorage(() => getStorage()),
  provideAuth(() => getAuth()),
  provideFunctions(() => getFunctions()),
  Title,
  {
    provide: LOGGER_METADATA_RESOLVER,
    useClass: PrincipleLoggerMetadataResolverService,
  },
  { provide: ErrorHandler, useClass: GCloudErrorHandlerService },
  {
    provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
    useValue: {
      duration: 3000,
    },
  },
  CurrentScopeFacade,
  {
    provide: USE_FIRESTORE_EMULATOR,
    useValue: environment.useEmulator ? ['localhost', 8080] : undefined,
  },
  {
    provide: USE_FUNCTIONS_EMULATOR,
    useValue: environment.useEmulator ? ['localhost', 5001] : undefined,
  },
  { provide: NG_MEDIPASS_CONFIG_PROVIDER, useClass: MedipassOrgConfigProvider },
  { provide: NG_TYRO_CONFIG_PROVIDER, useClass: TyroOrgConfigProvider },
  { provide: MAT_DATE_LOCALE, useValue: 'en-AU' },
  { provide: LOCALE_ID, useValue: 'en-AU' },
  { provide: DEFAULT_CURRENCY_CODE, useValue: '$' },
  {
    provide: FIREBASE_PROJECT_ID_PROVIDER,
    useValue: environment.firebase.projectId,
  },
  { provide: MAPS_CONFIG, useValue: environment.maps },
  {
    provide: USER_PROVIDED_META_REDUCERS,
    useFactory: () => principleCoreMetaReducers,
  },
  { provide: ORGANISATION_SERVICE, useExisting: OrganisationService },
  {
    provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
    useValue: { appearance: 'outline', floatLabel: 'auto' },
  },
  { provide: MAT_TABS_CONFIG, useValue: { stretchTabs: false } },
  {
    provide: AuthProcessService,
    useClass: MultifactorAwareAuthProcessService,
  },
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideAnimations(),
    LEGACY_MODULE_PROVIDERS,
    ...ROOT_SERVICES,
  ],
};
